import { Component } from '@theme/component';
import { isLowPowerDevice, clamp } from '@theme/utilities';

const RAIL_HALF_LENGTH = 3.2;
const RAIL_LENGTH = RAIL_HALF_LENGTH * 2 + 0.6;
const CARRIAGE_TRAVEL = RAIL_HALF_LENGTH - 0.55;
const BASE_TRAVEL_SPEED = 1.4;
const CAMERA_DISTANCE = 6.5;
const BASE_VERTICAL_FOV = 35;
// Keep ~8% clearance between the rail ends and the canvas edges - tight enough
// that the rail and carriage fill the frame instead of floating in empty space.
const RAIL_FIT_MARGIN = 1.08;
// Fixed horizontal half-angle (at CAMERA_DISTANCE) that keeps the rail
// within the frustum with the margin above, independent of aspect ratio.
const HORIZONTAL_HALF_FOV = Math.atan(((RAIL_LENGTH / 2) * RAIL_FIT_MARGIN) / CAMERA_DISTANCE);

/**
 * Builds a box-like geometry with chamfered edges along its depth axis, by
 * extruding a rectangular profile with `bevelEnabled`. Used for the carriage
 * body so it reads as a machined part rather than a flat-sided box.
 *
 * @param {import('three')} THREE
 * @param {number} width
 * @param {number} height
 * @param {number} depth
 * @param {number} bevelSize
 */
function createChamferedBoxGeometry(THREE, width, height, depth, bevelSize) {
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  const shape = new THREE.Shape();
  shape.moveTo(-halfWidth, -halfHeight);
  shape.lineTo(halfWidth, -halfHeight);
  shape.lineTo(halfWidth, halfHeight);
  shape.lineTo(-halfWidth, halfHeight);
  shape.lineTo(-halfWidth, -halfHeight);

  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(depth - bevelSize * 2, 0.001),
    bevelEnabled: true,
    bevelThickness: bevelSize,
    bevelSize,
    bevelSegments: 2,
  });
  geometry.center();
  return geometry;
}

/**
 * Renders an animated linear rail + carriage 3D scene. Three.js is only
 * imported and the scene only initialized once the component enters the
 * viewport, and never at all when the visitor prefers reduced motion —
 * the CSS/DOM fallback graphic (`refs.fallback`) stays visible instead.
 *
 * @typedef {object} Refs
 * @property {HTMLCanvasElement} canvas
 * @property {HTMLElement} [fallback]
 *
 * @extends {Component<Refs>}
 */
export class Hero3dComponent extends Component {
  requiredRefs = ['canvas'];

  /** @type {number} */
  #direction = 1;
  /** @type {boolean} */
  #started = false;
  /** @type {number} */
  #animationFrame = 0;
  /** @type {number} */
  #lastTime = 0;

  /** @type {import('three') | null} */
  #THREE = null;
  /** @type {any} */
  #renderer = null;
  /** @type {any} */
  #scene = null;
  /** @type {any} */
  #camera = null;
  /** @type {any} */
  #carriage = null;
  /** @type {any} */
  #envMap = null;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.#reducedMotionQuery.addEventListener('change', this.#handleReducedMotionChange);

    if (this.#reducedMotionQuery.matches) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#start();
          } else {
            this.#pause();
          }
        }
      },
      { rootMargin: '200px' }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#pause();
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#reducedMotionQuery.removeEventListener('change', this.#handleReducedMotionChange);
    this.#dispose();
  }

  #handleReducedMotionChange = () => {
    if (!this.#reducedMotionQuery.matches) return;

    this.#pause();
    this.#intersectionObserver?.disconnect();
    this.#dispose();
    this.refs.canvas.hidden = true;
    if (this.refs.fallback) this.refs.fallback.hidden = false;
  };

  async #start() {
    if (this.#started) {
      this.#resume();
      return;
    }
    this.#started = true;

    let THREE;
    try {
      THREE = await import('@theme/three');
    } catch (error) {
      console.warn('[hero-3d-component] Failed to load Three.js, keeping static fallback.', error);
      return;
    }

    // The component may have been removed from the DOM while the module was loading.
    if (!this.isConnected || this.#reducedMotionQuery.matches) return;

    this.#THREE = THREE;
    this.#setupScene(THREE);
    this.refs.canvas.hidden = false;
    if (this.refs.fallback) this.refs.fallback.hidden = true;
    this.#resume();
  }

  /**
   * @param {import('three')} THREE
   */
  #setupScene(THREE) {
    const canvas = this.refs.canvas;
    const lowPower = isLowPowerDevice();

    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: !lowPower,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, lowPower ? 1.25 : 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(BASE_VERTICAL_FOV, 1, 0.1, 100);
    // Lower and closer to the rail's own vertical center than a flat product
    // shot would be, so the frame reads as rail-and-carriage-filling rather
    // than a small subject floating in a large empty studio floor.
    camera.position.set(0, 1.35, CAMERA_DISTANCE);
    camera.lookAt(0, 0.3, 0);

    // Three-point studio lighting: a strong warm key (top/side), a soft neutral
    // fill from the opposite side to open up shadows without flattening them,
    // and a cool cyan rim for edge separation - a classic product-photography rig.
    scene.add(new THREE.AmbientLight(0xffffff, 0.12));

    const keyLight = new THREE.DirectionalLight(0xffd9a8, 3.2);
    keyLight.position.set(3, 5, 4);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xd8e4ff, 0.6);
    fillLight.position.set(-3.5, 1.5, 3);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0x3fd0e0, 1.4);
    rimLight.position.set(-4, 2, -3);
    scene.add(rimLight);

    // Cheap procedural environment map (no HDR download): baked once at setup
    // time, not per frame, so its cost is negligible even though the internal
    // PMREM capture always happens at a fixed 256px cube size. Skipped entirely
    // on low-power devices, where the extra per-pixel envMap sampling on every
    // material, every frame, is the part that actually isn't worth it.
    let envRenderTarget = null;
    if (!lowPower) {
      envRenderTarget = this.#createEnvironmentMap(THREE, renderer);
      scene.environment = envRenderTarget.texture;
    }

    const railLength = RAIL_LENGTH;

    const railMaterial = new THREE.MeshStandardMaterial({ color: 0xb7bdc4, metalness: 0.85, roughness: 0.28 });
    const rail = new THREE.Mesh(new THREE.BoxGeometry(railLength, 0.22, 0.5), railMaterial);
    scene.add(rail);

    const grooveMaterial = new THREE.MeshStandardMaterial({ color: 0x8a9097, metalness: 0.9, roughness: 0.22 });
    const groove = new THREE.Mesh(new THREE.BoxGeometry(railLength, 0.06, 0.18), grooveMaterial);
    groove.position.y = 0.14;
    scene.add(groove);

    const endCapMaterial = new THREE.MeshStandardMaterial({ color: 0x6b7076, metalness: 0.75, roughness: 0.38 });
    for (const side of [-1, 1]) {
      const endCap = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.34, 0.56), endCapMaterial);
      endCap.position.x = side * (railLength / 2 - 0.05);
      scene.add(endCap);
    }

    const carriage = new THREE.Group();

    // Chamfered body: a rectangular profile extruded across the carriage's
    // depth with a bevel on the extrusion ends, so the block reads as a
    // machined part with softened edges instead of a flat, sharp-cornered
    // box. No rounded-box geometry addon ships with this theme's core-only
    // three.js build, so it's built here with THREE.Shape + ExtrudeGeometry.
    const bodyGeometry = createChamferedBoxGeometry(THREE, 1.1, 0.62, 0.9, 0.035);
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0x1a1d21, metalness: 0.55, roughness: 0.35 });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.position.y = 0.42;
    carriage.add(body);

    // Light metallic end plates - the design system's neutral metallic tone,
    // replacing the previous red/green axis-gizmo-style end caps.
    const endPlateMaterial = new THREE.MeshStandardMaterial({ color: 0x8a8d91, metalness: 0.8, roughness: 0.3 });
    for (const side of [-1, 1]) {
      const endPlate = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.5, 0.72), endPlateMaterial);
      endPlate.position.set(side * 0.55, 0.42, 0);
      carriage.add(endPlate);
    }

    // A single orange brand-accent stripe stands in for the old two-tone
    // red/green blocks, plus a small cyan data-plate detail on the front face.
    const accentStripeMaterial = new THREE.MeshStandardMaterial({ color: 0xff5a1f, metalness: 0.2, roughness: 0.45 });
    const accentStripe = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.05, 0.92), accentStripeMaterial);
    accentStripe.position.y = 0.6;
    carriage.add(accentStripe);

    const dataPlateMaterial = new THREE.MeshStandardMaterial({ color: 0x00c2d9, metalness: 0.1, roughness: 0.5 });
    const dataPlate = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.14, 0.02), dataPlateMaterial);
    dataPlate.position.set(0, 0.32, 0.46);
    carriage.add(dataPlate);

    // Thin wiper-seal grooves along the bottom front/back edges, where a real
    // linear block's ball-bearing raceway meets the rail.
    const wiperMaterial = new THREE.MeshStandardMaterial({ color: 0x0b0e11, metalness: 0.1, roughness: 0.85 });
    for (const z of [-0.44, 0.44]) {
      const wiper = new THREE.Mesh(new THREE.BoxGeometry(1.02, 0.035, 0.05), wiperMaterial);
      wiper.position.set(0, 0.12, z);
      carriage.add(wiper);
    }

    // Small bolt heads catch the environment map's highlights, adding the tiny
    // glints a real macro product photo would have.
    if (!lowPower) {
      const boltMaterial = new THREE.MeshStandardMaterial({ color: 0x9aa1a8, metalness: 0.9, roughness: 0.25 });
      const boltGeometry = new THREE.CylinderGeometry(0.035, 0.035, 0.05, 12);
      for (const x of [-0.4, 0.4]) {
        for (const z of [-0.32, 0.32]) {
          const bolt = new THREE.Mesh(boltGeometry, boltMaterial);
          bolt.position.set(x, 0.74, z);
          carriage.add(bolt);
        }
      }
    }

    scene.add(carriage);

    // Large, subtly reflective floor beneath the rail. A true mirror reflection
    // (second render-to-texture pass of the flipped scene, blurred) would double
    // the per-frame draw cost for a fairly subtle payoff; a low-roughness PBR
    // material lit by the same environment map instead gives a convincing soft
    // reflective-tabletop look for a fraction of the cost - the better trade-off
    // for a scene that has to keep animating smoothly on mid-range phones.
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x0b0e11,
      metalness: 0.5,
      roughness: 0.3,
      envMapIntensity: 1.1,
    });
    const floorSize = railLength + 1.4;
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(floorSize, floorSize * 0.55), floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.16;
    scene.add(floor);

    // Faint cyan blueprint grid on the floor: reinforces the CNC/engineering
    // aesthetic and fills what would otherwise be flat, empty floor space.
    // GridHelper's material is a single unlit LineBasicMaterial, so it costs
    // nothing per-pixel and is safe to keep on low-power devices too.
    const grid = new THREE.GridHelper(floorSize, 14, 0x00c2d9, 0x00c2d9);
    grid.material.transparent = true;
    grid.material.opacity = 0.12;
    grid.position.y = floor.position.y + 0.001;
    scene.add(grid);

    this.#renderer = renderer;
    this.#scene = scene;
    this.#camera = camera;
    this.#carriage = carriage;
    this.#envMap = envRenderTarget;

    this.#resizeObserver = new ResizeObserver(() => this.#handleResize());
    this.#resizeObserver.observe(this);
    this.#handleResize();
  }

  /**
   * Bakes a tiny procedural gradient scene into a PMREM environment map, so PBR
   * materials pick up soft, realistic reflections/highlights without downloading
   * an HDR file. The gradient is a 1px-wide strip - PMREM convolves everything
   * into blurry mip levels anyway, so any extra detail would be wasted work.
   *
   * Returns the render target itself (not just its `.texture`) because that's
   * what needs to be disposed later - `renderTarget.dispose()` is the pattern
   * three.js's own PMREM examples use to release the underlying framebuffer.
   *
   * @param {import('three')} THREE
   * @param {any} renderer
   */
  #createEnvironmentMap(THREE, renderer) {
    const pmremGenerator = new THREE.PMREMGenerator(renderer);

    const envScene = new THREE.Scene();

    const gradientCanvas = document.createElement('canvas');
    gradientCanvas.width = 1;
    gradientCanvas.height = 64;
    const ctx = gradientCanvas.getContext('2d');
    if (ctx) {
      const gradient = ctx.createLinearGradient(0, 0, 0, 64);
      gradient.addColorStop(0, '#fff2df'); // warm key highlight ("sky")
      gradient.addColorStop(0.4, '#2a2f36'); // graphite studio wall
      gradient.addColorStop(0.75, '#0e1013'); // dark studio floor
      gradient.addColorStop(1, '#5fd8ea'); // cool cyan rim/bounce
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 1, 64);
    }

    const gradientTexture = new THREE.CanvasTexture(gradientCanvas);
    gradientTexture.colorSpace = THREE.SRGBColorSpace;

    const envSphere = new THREE.Mesh(
      new THREE.SphereGeometry(10, 8, 8),
      new THREE.MeshBasicMaterial({ map: gradientTexture, side: THREE.BackSide })
    );
    envScene.add(envSphere);

    const renderTarget = pmremGenerator.fromScene(envScene, 0.03, 0.1, 50);

    gradientTexture.dispose();
    envSphere.geometry.dispose();
    /** @type {any} */ (envSphere.material).dispose();
    pmremGenerator.dispose();

    return renderTarget;
  }

  #handleResize() {
    if (!this.#renderer || !this.#camera) return;

    const width = Math.max(this.clientWidth, 1);
    const height = Math.max(this.clientHeight, 1);
    const aspect = width / height;

    this.#renderer.setSize(width, height, false);
    this.#camera.aspect = aspect;

    // The rail's on-screen width depends on both aspect ratio and vertical
    // FOV. Re-derive the vertical FOV from the fixed horizontal half-angle
    // so the rail always fits inside the canvas with margin, whether the
    // container is short and wide (mobile) or tall and narrow (desktop
    // columns).
    const verticalFovRad = 2 * Math.atan(Math.tan(HORIZONTAL_HALF_FOV) / aspect);
    this.#camera.fov = clamp((verticalFovRad * 180) / Math.PI, 20, 90);
    this.#camera.updateProjectionMatrix();
  }

  #resume() {
    if (this.#animationFrame || !this.#renderer) return;
    this.#lastTime = performance.now();
    this.#animationFrame = requestAnimationFrame(this.#tick);
  }

  #pause() {
    if (!this.#animationFrame) return;
    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = 0;
  }

  /** @param {number} time */
  #tick = (time) => {
    const delta = Math.min((time - this.#lastTime) / 1000, 0.1);
    this.#lastTime = time;

    if (this.#carriage) {
      this.#carriage.position.x += this.#direction * delta * BASE_TRAVEL_SPEED;

      if (this.#carriage.position.x > CARRIAGE_TRAVEL) {
        this.#carriage.position.x = CARRIAGE_TRAVEL;
        this.#direction = -1;
      } else if (this.#carriage.position.x < -CARRIAGE_TRAVEL) {
        this.#carriage.position.x = -CARRIAGE_TRAVEL;
        this.#direction = 1;
      }
    }

    this.#renderer.render(this.#scene, this.#camera);
    this.#animationFrame = requestAnimationFrame(this.#tick);
  };

  #dispose() {
    if (!this.#renderer) {
      this.#started = false;
      return;
    }

    this.#scene?.traverse((/** @type {any} */ object) => {
      object.geometry?.dispose();
      const material = object.material;
      if (!material) return;
      const materials = Array.isArray(material) ? material : [material];
      materials.forEach((mat) => mat.dispose());
    });

    this.#envMap?.dispose();
    this.#envMap = null;

    this.#renderer.dispose();
    this.#renderer = null;
    this.#scene = null;
    this.#camera = null;
    this.#carriage = null;
    this.#THREE = null;
    this.#started = false;
  }
}

if (!customElements.get('hero-3d-component')) {
  customElements.define('hero-3d-component', Hero3dComponent);
}
